/*
 * Fig & Bloom — R4: scope a suburb/city SEO page's product grid to the fulfilment
 * location that serves the page's postcode, and pre-scope the delivery popup.
 *
 * Rendered by snippets/city-product-grid.liquid, which is used by seven metaobject
 * templates = all 1,364 published SEO pages.
 *
 * WHY THIS IS JAVASCRIPT
 * Liquid cannot resolve a postcode. The postcode tables are JS-only and the location
 * registry lives in the Delivery Date App's APP-SCOPED metafield, which the theme
 * cannot read. So the page states its postcode and we resolve it here with the same
 * window.fbResolvePostcode() / window.fbLocationId() the collection pages, the cart
 * extension and the app's sliders already use.
 *
 * FAIL OPEN, ALWAYS
 * Every failure path leaves the grid exactly as the server rendered it: the first 12
 * products, unfiltered. That is deliberate. This is a marketing page, not an
 * add-to-cart gate — showing the national range beats showing an empty page, and the
 * real protection is the cart's fail-closed rule, which is already live.
 *
 * THE HIDE RULE MIRRORS THE ADD-TO-CART GATE EXACTLY
 * (assets/product-form.js:773-777)
 *     managed = the product declares `true` for at least one location
 *     hide when: managed && an attribute exists for THIS location && it is not 'true'
 * A product with NO flag for this location stays VISIBLE. That is the client's
 * decision of 1 Aug 2026 — the opt-in "missing flag = unavailable" change was built
 * and then reverted (it would have hidden 152 products in Adelaide alone). Do not
 * tighten this without new data.
 *
 * Mirroring matters in both directions: if this were more permissive than the gate a
 * shopper would see a tile they cannot add; if it were stricter we would hide
 * sellable stock.
 */
(function () {
  'use strict';

  var GRID_ID = 'city-products';
  var COOKIE_DAYS = 14;

  /* Same five as the rest of the codebase (delivery-suburb-popup.js addLocationToUrl).
     Only a fallback: window.fbLocationId is authoritative and covers new cities with
     no code change. This exists for a store whose app config has not regenerated. */
  var STATIC_LOCATION_IDS = {
    Brisbane: '70541672681',
    Melbourne: '70507725033',
    Sydney: '70541639913',
    RestofAustralia: '74290069737',
    Adelaide: '90760315113'
  };

  function getCookie(name) {
    var parts = ('; ' + document.cookie).split('; ' + name + '=');
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  }

  /* Byte-for-byte the popup's own setter (delivery-suburb-popup.js:1203) — same
     path, same expiry, same lack of encoding — so a seeded cookie is indistinguishable
     from one the popup wrote. */
  function setCookie(name, value, days) {
    var expires = '';
    if (days) {
      var d = new Date();
      d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
      expires = '; expires=' + d.toUTCString();
    }
    document.cookie = name + '=' + (value || '') + expires + '; path=/';
    // One copy per name (snippets/fb-delivery-date-guard.liquid).
    if (window.fbDeliveryDate && window.fbDeliveryDate.settle) window.fbDeliveryDate.settle(name);
  }

  /* window.fbResolvePostcode has TWO failure modes and only one of them is falsy:
       null              -> no app config on the page at all
       'Unknown Location'-> a complete config that matched nothing
     The string is truthy. Treating it as a location cost a real bug in the cart
     extension on 1 Aug 2026. Both cases mean "we cannot place this page" -> fail open. */
  function resolveLocation(postcode) {
    if (typeof window.fbResolvePostcode !== 'function') return null;
    var loc = window.fbResolvePostcode(postcode);
    if (!loc || loc === 'Unknown Location') return null;
    return loc;
  }

  function locationIdFor(location) {
    var id = null;
    if (typeof window.fbLocationId === 'function') id = window.fbLocationId(location);
    return id || STATIC_LOCATION_IDS[location] || null;
  }

  /* WHICH RULE DECIDES "available from this location" — read before changing.
   *
   * false (current): hide only an EXPLICIT non-true. A product with no flag for this
   *   location stays visible. Mirrors the add-to-cart gate, so a shopper never sees a
   *   tile they cannot add. This is the client's decision of 1 Aug 2026.
   *
   * true: show ONLY an explicit `true`. This is what the Search & Discovery filter
   *   does on /collections/flowers (filter.v.m.location.{id}=1), and it is the literal
   *   reading of the requirements doc: "renders only products available from that
   *   location".
   *
   * Measured on prod for the 24 products this grid fetches, the two rules are
   * IDENTICAL for every location that has suburb pages today:
   *     Melbourne 22/22   Sydney 24/24   Brisbane 23/23   RestofAustralia 13/12
   * They diverge only for a location with little or no tagging — Adelaide 23 vs 1.
   *
   * ⚠️ THE GAP THIS LEAVES: the doc's acceptance criterion is "a GOLD COAST suburb
   * page shows only Gold Coast-fulfillable products". Gold Coast does not exist yet.
   * On the day it does, none of its products will be flagged, so with `false` its
   * suburb pages would show the whole national range — failing that criterion until
   * tagging is complete. Flipping this to true satisfies the criterion but would hide
   * any product that is genuinely sellable yet untagged (Melbourne has 17 such of
   * 179), which is the exact trade-off the client rejected for add-to-cart.
   *
   * This is a business decision, not a technical one. Flip the one constant. */
  var REQUIRE_EXPLICIT_AVAILABILITY = false;

  function isBlockedForLocation(col, locId) {
    var attr = 'data-avail-' + locId;
    if (REQUIRE_EXPLICIT_AVAILABILITY) return col.getAttribute(attr) !== 'true';
    if (col.getAttribute('data-loc-managed') !== 'true') return false;
    if (!col.hasAttribute(attr)) return false;
    return col.getAttribute(attr) !== 'true';
  }

  /* The requirements doc says a suburb page resolves "its postcode(s)". The field is a
     single_line_text_field (verified against the metaobject definitions for vic/nsw/qld),
     so it is one value in practice — but free text can hold "3168, 3169" or "VIC 3168",
     and handing either straight to the resolver would match nothing and silently fall
     back to the unfiltered grid. Take the first 4-digit run instead. */
  function firstPostcode(raw) {
    var m = String(raw || '').match(/\b\d{4}\b/);
    return m ? m[0] : '';
  }

  function scope(grid) {
    var postcode = firstPostcode(grid.getAttribute('data-page-postcode'));
    /* No postcode: the 11 city and 69 hospital metaobjects have no postcode FIELD at
       all (not merely an empty value — confirmed against the metaobject definitions),
       so this is the permanent state for them until Daniel adds one. Leave the page
       exactly as it is; the same code covers them the day the field appears. */
    if (!postcode) return { status: 'no-postcode' };

    var location = resolveLocation(postcode);
    if (!location) return { status: 'unresolved', postcode: postcode };

    var locId = locationIdFor(location);
    if (!locId) return { status: 'no-location-id', location: location };

    var showCount = parseInt(grid.getAttribute('data-show-count'), 10) || 12;
    var cols = grid.querySelectorAll('.col[data-product-id]');
    var shown = 0;
    var blocked = 0;

    for (var i = 0; i < cols.length; i++) {
      if (isBlockedForLocation(cols[i], locId)) {
        cols[i].hidden = true;
        blocked++;
      } else if (shown < showCount) {
        cols[i].hidden = false;
        shown++;
      } else {
        cols[i].hidden = true;
      }
    }

    /* Nothing survived. Rather than an empty box, say we deliver here and let the
       "View all flowers" button carry them onward. Should not happen for NSW/VIC/QLD
       (Melbourne 162, Sydney 169, Brisbane 158 of 179 products are tagged). */
    var empty = grid.querySelector('.city-grid__empty');
    if (empty) empty.hidden = shown !== 0;

    grid.setAttribute('data-fb-scoped', location);
    return {
      status: 'scoped',
      location: location,
      locationId: locId,
      total: cols.length,
      shown: shown,
      blocked: blocked
    };
  }

  /* DELIBERATELY NOT SEEDING THE DELIVERY COOKIES — do not re-add this.

     R4 originally seeded deliverySuburb/deliveryPostcode/deliveryState here so the
     popup would open straight at the DATE step on an SEO page. It worked, but the
     cookies are GLOBAL, PERSISTENT (14 days) state, and a second, older consumer reads
     them: RedirectManager.checkAndRedirect() and applyLocationFilterInPlace() in
     assets/delivery-suburb-popup.js rewrite /collections/* to
     ?filter.v.m.location.{id}=1 whenever both cookies exist. They cannot tell a
     location the customer CHOSE from one a page load inferred.

     Measured on production 3 Aug 2026, with no user interaction at all:
       land on /pages/vic/clayton  -> /collections/flowers showed 35 of 50 products
       land on /pages/city/adelaide -> /collections/flowers showed 0 of 50 ("No products
                                       found"), because only 4 of 162 products carry an
                                       Adelaide flag
     A visitor who never picked anything got a silently narrowed — sometimes empty — shop.

     Client decision (Daniel, 3 Aug 2026): require the customer to enter their location
     rather than pre-filling it. That supersedes the "popup is pre-scoped so the user
     only supplies a date" clause of Requirement 4 in fulfilment-locations-requirements.pdf.

     The page still scopes its OWN grid from data-page-postcode (see scope() above) —
     that needs no cookie. The cookies are now written only by an explicit choice in the
     popup (assets/delivery-suburb-popup.js:1937, :2482, :2524). */

  /* The resolver is defined by an inline script the app block injects, so it is
     normally present by the time a deferred script runs. Poll briefly rather than
     assume it, and give up quietly — giving up means the unfiltered grid, which is
     the current behaviour. */
  function whenResolverReady(cb) {
    var tries = 0;
    (function poll() {
      if (typeof window.fbResolvePostcode === 'function' || tries >= 20) return cb();
      tries++;
      setTimeout(poll, 100);
    })();
  }

  function init() {
    var grid = document.getElementById(GRID_ID);
    if (!grid) return;
    whenResolverReady(function () {
      var result = scope(grid);
      /* result.seeded is retained and always false: the diagnostic shape of
         window.fbSuburbGrid is asserted by tests/fb-suburb-grid.test.js and is what
         we read when verifying a page in the browser. */
      result.seeded = false;
      window.fbSuburbGrid = result;
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
